import { useToast } from "@gearbox-protocol/deploy-tools-ui";
import type { FC, PropsWithChildren } from "react";
import { useEffect, useMemo, useState } from "react";
import { useEffectOnce } from "react-use";

import { api } from "./Api";
import { EVENT_SIGNIN, EVENT_SIGNOUT } from "./constants";
import type { User } from "./types";
import { AuthCtx, useAuth } from "./useAuth";

export const AuthProvider: FC<PropsWithChildren> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const { toast } = useToast();

  useEffectOnce(() => {
    api.refresh();
  });

  useEffect(() => {
    const onSignin = (e: Event) => {
      setUser((e as CustomEvent<User>).detail);
    };
    const onSignout = () => {
      setUser(null);
    };
    api.addEventListener(EVENT_SIGNIN, onSignin);
    api.addEventListener(EVENT_SIGNOUT, onSignout);
    return () => {
      api.removeEventListener(EVENT_SIGNIN, onSignin);
      api.removeEventListener(EVENT_SIGNOUT, onSignout);
    };
  }, []);

  const ctx = useMemo(
    () => ({
      user,
      signIn: async () => {
        try {
          await api.signIn();
        } catch (e) {
          toast({
            variant: "destructive",
            title: "Error",
            description: `${e}`,
          });
        }
      },
      signOut: async () => {
        await api.signOut();
      },
    }),
    [user, toast],
  );

  return <AuthCtx.Provider value={ctx}>{children}</AuthCtx.Provider>;
};

export const AdminOnly: FC<PropsWithChildren> = ({ children }) => {
  const { user } = useAuth();
  return user ? children : null;
};
